import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  iOfflineList,
  iScanInfo,
  iScanResult,
} from 'services/Ticketing/Ticketing.type';
import {
  ScannerCamera,
  ScannerResultOverlay,
  ScannerManualInput,
  ScannerStatusBar,
  ScannerFooter,
} from './components';
import {
  LookupResult,
  ScanResult,
  TicketInfo,
  codeFromScan,
} from './components/scannerUtils';

type Counters = iScanInfo['counters'];
type OfflineTicket = iOfflineList['tickets'][number];

export interface ScannerApi {
  storageKey: string;
  scan: (code: string, device: string) => Promise<{ data: iScanResult }>;
  sync: (
    scans: Array<{ code: string; at: string }>,
    device: string
  ) => Promise<{ counters: Counters }>;
  offline: () => Promise<iOfflineList>;
  lookup?: (q: string) => Promise<{
    results: LookupResult[];
  }>;
}

interface ScannerViewProps {
  api: ScannerApi;
  title: string;
  subtitle?: string;
  timezone: string;
  eventDays: string[];
  initialCounters: Counters;
  onBack?: () => void;
  onRejected?: (status: number, message: string) => void;
}

const CATEGORY: Record<string, string> = {
  visitor: 'Visiteur',
  exhibitor: 'Exposant',
  setup: 'Montage / démontage',
  organiser: 'Organisateur',
};

const MESSAGES: Record<string, string> = {
  ok: 'Entrée validée',
  reentry: 'Ré-entrée',
  already_in: "Déjà entré aujourd'hui",
  wrong_day: 'Pas valable ce jour',
  wrong_gate: 'Billet non accepté à ce point de contrôle',
  cancelled: 'Billet annulé',
  unpaid: 'Billet non payé',
  unknown: 'Code inconnu',
};

const store = {
  get<T>(key: string, fallback: T): T {
    try {
      const v = localStorage.getItem(key);
      return v ? (JSON.parse(v) as T) : fallback;
    } catch {
      return fallback;
    }
  },
  set(key: string, value: unknown) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  },
};

async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(text)
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function todayIn(tz: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function beep(ok: boolean): void {
  try {
    const ctx = new AudioContext();
    const o = ctx.createOscillator();
    o.frequency.value = ok ? 880 : 220;
    o.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + (ok ? 0.12 : 0.35));
  } catch {
    /* no sound */
  }
  try {
    navigator.vibrate?.(ok ? 80 : [120, 60, 120]);
  } catch {
    /* no vibration */
  }
}

export default function ScannerView({
  api,
  title,
  subtitle,
  timezone,
  eventDays,
  initialCounters,
  onBack,
  onRejected,
}: ScannerViewProps): JSX.Element {
  const keyList = `${api.storageKey}.list`;
  const keyQueue = `${api.storageKey}.queue`;
  const keySeen = `${api.storageKey}.seen`;

  const device = useRef(
    store.get<string>('em.scan.device', '') ||
      `Poste ${Math.floor(Math.random() * 900 + 100)}`
  );

  const [camera, setCamera] = useState<'off' | 'starting' | 'on' | 'error'>(
    'off'
  );
  const [result, setResult] = useState<ScanResult | null>(null);
  const [counters, setCounters] = useState<Counters>(initialCounters);
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const [list, setList] = useState<iOfflineList | null>(() =>
    store.get(keyList, null)
  );
  const [queue, setQueue] = useState<Array<{ code: string; at: string }>>(() =>
    store.get(keyQueue, [])
  );

  const busy = useRef(false);
  const lastCode = useRef<{ code: string; at: number } | null>(null);

  useEffect(() => {
    store.set('em.scan.device', device.current);
  }, []);

  useEffect(() => {
    const up = (): void => setOnline(true);
    const down = (): void => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const downloadList = useCallback(async () => {
    try {
      const r = await api.offline();
      setList(r);
      store.set(keyList, r);
    } catch {
      /* keep the previous list */
    }
  }, [api, keyList]);

  useEffect(() => {
    downloadList();
    const id = window.setInterval(downloadList, 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [downloadList]);

  const sync = useCallback(async () => {
    const pending = store.get<Array<{ code: string; at: string }>>(
      keyQueue,
      []
    );
    if (!pending.length) return;
    try {
      const r = await api.sync(pending, device.current);
      store.set(keyQueue, []);
      setQueue([]);
      setCounters(r.counters);
    } catch {
      /* retry later */
    }
  }, [api, keyQueue]);

  useEffect(() => {
    if (online) sync();
    const id = window.setInterval(
      async () => await (navigator.onLine && sync()),
      30000
    );
    return () => window.clearInterval(id);
  }, [online, sync]);

  const offlineCheck = async (code: string): Promise<ScanResult> => {
    const at = new Date().toISOString();
    const nextQueue = [
      ...store.get<Array<{ code: string; at: string }>>(keyQueue, []),
      { code, at },
    ];
    store.set(keyQueue, nextQueue);
    setQueue(nextQueue);

    const h = await sha256(code);
    const t = list?.tickets.find((x) => x.h === h);
    const day = todayIn(timezone);

    if (!t)
      return {
        result: 'unknown',
        accepted: false,
        message: `${MESSAGES.unknown} (liste hors ligne)`,
        ticket: null,
        previous_entry_at: null,
        offline: true,
      };

    const days = t.d.length ? t.d : eventDays;
    const ticket: TicketInfo = {
      code,
      name: t.n,
      company: t.c,
      job_title: null,
      type: t.t,
      category: t.k,
      days,
      status: 'valid',
    };

    if (t.g === false)
      return {
        result: 'wrong_gate',
        accepted: false,
        message: MESSAGES.wrong_gate,
        ticket,
        previous_entry_at: null,
        offline: true,
      };

    if (!days.includes(day))
      return {
        result: 'wrong_day',
        accepted: false,
        message: MESSAGES.wrong_day,
        ticket,
        previous_entry_at: null,
        offline: true,
      };

    const seen = store.get<Record<string, string>>(keySeen, {});
    const key = `${day}:${h}`;
    const previous = seen[key] ?? null;
    if (!previous) store.set(keySeen, { ...seen, [key]: at });
    const r = previous ? (t.r ? 'reentry' : 'already_in') : 'ok';

    return {
      result: r,
      accepted: r !== 'already_in',
      message: MESSAGES[r],
      ticket,
      previous_entry_at: previous,
      offline: true,
    };
  };

  const check = useCallback(
    async (raw: string) => {
      const code = codeFromScan(raw);
      if (!code || busy.current) return;

      if (
        lastCode.current &&
        lastCode.current.code === code &&
        Date.now() - lastCode.current.at < 4000
      )
        return;

      lastCode.current = { code, at: Date.now() };
      busy.current = true;

      let r: ScanResult;
      try {
        if (!navigator.onLine) throw new Error('offline');
        const res = await api.scan(code, device.current);
        r = res.data;
        if (res.data.counters) setCounters(res.data.counters);
      } catch (e: any) {
        const status: number = e?.response?.status ?? 0;
        if ([401, 403, 404, 410].includes(status) && onRejected) {
          busy.current = false;
          onRejected(status, e.response.data?.message ?? 'Accès refusé');
          return;
        }
        r =
          e?.response &&
          e.response.status !== 0 &&
          e.response.status < 500 &&
          e.response.status !== 419
            ? {
                result: 'unknown',
                accepted: false,
                message: e.response.data?.message ?? 'Erreur',
                ticket: null,
                previous_entry_at: null,
              }
            : await offlineCheck(code);
      }

      setResult(r);
      beep(r.accepted);
      busy.current = false;
    },
    [api, list, eventDays, onRejected, timezone]
  );

  const checkRef = useRef(check);
  checkRef.current = check;

  useEffect(() => {
    if (!result?.accepted) return;
    const id = window.setTimeout(() => setResult(null), 2500);
    return () => window.clearTimeout(id);
  }, [result]);

  const handleManualScan = (code: string): void => {
    lastCode.current = null;
    check(code);
  };

  const appStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: '#030712',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Inter, Arial, sans-serif',
  };

  const mainStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 16,
    gap: 16,
    width: '100%',
    maxWidth: 560,
    margin: '0 auto',
    boxSizing: 'border-box',
  };

  return (
    <div style={appStyle}>
      <ScannerStatusBar
        title={title}
        subtitle={subtitle}
        online={online}
        list={list}
        queueLength={queue.length}
        counters={counters}
        hasCheckpointStats={counters.entries_here_today !== undefined}
        onBack={onBack}
      />

      <main style={mainStyle}>
        <div style={{ position: 'relative', width: '100%' }}>
          <ScannerCamera
            onScan={check}
            busyRef={busy}
            cameraState={camera}
            setCameraState={setCamera}
          />

          {result && (
            <ScannerResultOverlay
              result={result}
              onDismiss={() => setResult(null)}
            />
          )}
        </div>

        <ScannerManualInput
          api={api}
          onScan={handleManualScan}
          hasLookup={!!api.lookup}
        />

        <ScannerFooter
          device={device.current}
          list={list}
          onRefresh={downloadList}
        />
      </main>
    </div>
  );
}
